import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, ArrowUpRight, ArrowDownRight, Wallet, ScrollText, Download,
} from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const chf = (n) => new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(n || 0);
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ type: "income", amount: "", person: "", date: today(), purpose: "", place: "" });

const fieldClass = "h-11 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60";

const FinancePanel = ({ onLogout }) => {
  const [txns, setTxns] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/finance/transactions`, { headers: authHeader() });
      setTxns(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Buchungen konnten nicht geladen werden.");
      setTxns([]);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openAdd = (type) => { setForm({ ...emptyForm(), type }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    const amount = Number(form.amount);
    if (!amount || amount <= 0) { toast.error("Bitte einen gültigen Betrag eingeben."); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/finance/transactions`, {
        type: form.type,
        amount,
        person: form.person.trim() || null,
        purpose: form.purpose.trim() || null,
        place: form.place.trim() || null,
        date: form.date || today(),
      }, { headers: authHeader() });
      setTxns((prev) => [data, ...prev]);
      toast.success(form.type === "income" ? "Einzahlung erfasst." : "Auszahlung erfasst.");
      setDialogOpen(false);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/finance/transactions/${id}`, { headers: authHeader() });
      setTxns((prev) => prev.filter((t) => t.id !== id));
      toast.success("Buchung gelöscht.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  const exportCSV = () => {
    downloadCSV(
      `boe_finanzen_${today()}.csv`,
      [
        { key: "date", label: "Datum", format: (v) => fmtDate(v) },
        { key: "type", label: "Typ", format: (v) => (v === "income" ? "Einzahlung" : "Auszahlung") },
        { key: "amount", label: "Betrag (CHF)", format: (v, row) => `${row.type === "income" ? "" : "-"}${Number(v || 0).toFixed(2)}` },
        { key: "person", label: "Von / An" },
        { key: "purpose", label: "Zweck" },
        { key: "place", label: "Ort" },
      ],
      txns,
    );
  };

  if (txns === null) {
    return <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>;
  }

  const income = txns.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const expense = txns.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
  const balance = income - expense;

  return (
    <div data-testid="finance-panel">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10">
        <div>
          <span className="section-eyebrow">Kasse</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Finanzen</h1>
          <p className="mt-2 text-sm text-cream/60">Kontostand wird automatisch aus Ein- und Auszahlungen berechnet.</p>
        </div>

        {/* Balance card (top right) */}
        <div className="rounded-2xl border border-gold/30 bg-white/[0.04] px-7 py-5 min-w-[260px]" data-testid="finance-balance">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold" style={{ color: "#C69C22" }}>
            <Wallet size={15} /> Kontostand
          </div>
          <p className={`mt-1 text-4xl font-semibold tracking-tight ${balance < 0 ? "text-red-400" : "text-cream"}`}>
            {chf(balance)}
          </p>
          <div className="mt-3 flex items-center gap-5 text-sm">
            <span className="inline-flex items-center gap-1.5 text-emerald-400"><ArrowUpRight size={15} /> {chf(income)}</span>
            <span className="inline-flex items-center gap-1.5 text-red-400"><ArrowDownRight size={15} /> {chf(expense)}</span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button onClick={() => openAdd("income")} data-testid="finance-add-income" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 text-sm font-semibold transition-colors">
          <Plus size={16} /> Einzahlung
        </button>
        <button onClick={() => openAdd("expense")} data-testid="finance-add-expense" className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-sm font-semibold transition-colors">
          <Plus size={16} /> Auszahlung
        </button>
        <button onClick={exportCSV} disabled={txns.length === 0} data-testid="finance-export-csv" className="ml-auto inline-flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-sm font-semibold text-gold-soft hover:bg-gold hover:text-anthracite transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gold-soft" style={{ color: "#E5C76B" }}>
          <Download size={16} /> CSV-Export
        </button>
      </div>

      {/* Logbuch */}
      <div className="flex items-center gap-2 mb-4 text-cream/70">
        <ScrollText size={16} style={{ color: "#E5C76B" }} />
        <h2 className="text-sm uppercase tracking-[0.22em] font-semibold">Logbuch · {txns.length} Buchungen</h2>
      </div>

      {txns.length === 0 ? (
        <p className="text-sm text-cream/45 py-12 text-center border border-dashed border-white/10 rounded-2xl" data-testid="finance-empty">
          Noch keine Buchungen. Erfasse oben eine Einzahlung oder Auszahlung.
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gold/15" data-testid="finance-log">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-cream/55 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-5 py-3">Typ</th>
                <th className="text-right font-semibold px-5 py-3">Betrag</th>
                <th className="text-left font-semibold px-5 py-3">Von / An</th>
                <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Datum</th>
                <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">Zweck</th>
                <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">Ort</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => {
                const isIncome = t.type === "income";
                return (
                  <tr key={t.id} data-testid={`finance-row-${t.id}`} className="border-t border-white/8 hover:bg-white/[0.02]">
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${isIncome ? "text-emerald-400" : "text-red-400"}`}>
                        {isIncome ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                        {isIncome ? "Einzahlung" : "Auszahlung"}
                      </span>
                    </td>
                    <td className={`px-5 py-3 text-right font-semibold whitespace-nowrap ${isIncome ? "text-emerald-400" : "text-red-400"}`}>
                      {isIncome ? "+" : "−"}{chf(t.amount)}
                    </td>
                    <td className="px-5 py-3 text-cream/85">{t.person || "—"}</td>
                    <td className="px-5 py-3 text-cream/65 hidden md:table-cell whitespace-nowrap">{fmtDate(t.date)}</td>
                    <td className="px-5 py-3 text-cream/65 hidden lg:table-cell">{t.purpose || "—"}</td>
                    <td className="px-5 py-3 text-cream/65 hidden lg:table-cell">{t.place || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`finance-delete-${t.id}`} disabled={deletingId === t.id} aria-label="Buchung löschen" className="text-cream/40 hover:text-red-400 transition-colors disabled:opacity-50">
                            {deletingId === t.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-cream">Buchung löschen?</AlertDialogTitle>
                            <AlertDialogDescription className="text-cream/60">
                              {isIncome ? "Einzahlung" : "Auszahlung"} über <span className="text-cream/90 font-medium">{chf(t.amount)}</span> wirklich löschen? Der Kontostand wird angepasst.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                            <AlertDialogAction data-testid={`finance-delete-confirm-${t.id}`} onClick={() => remove(t.id)} className="bg-red-600 text-white hover:bg-red-700">Löschen</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-anthracite-soft border border-gold/20 text-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cream flex items-center gap-2">
              {form.type === "income"
                ? <><ArrowUpRight size={18} className="text-emerald-400" /> Einzahlung erfassen</>
                : <><ArrowDownRight size={18} className="text-red-400" /> Auszahlung erfassen</>}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="finance-form">
            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-white/5 border border-white/10">
              <button type="button" onClick={() => setForm((f) => ({ ...f, type: "income" }))} data-testid="finance-form-type-income"
                className={`py-2 rounded-lg text-sm font-semibold transition-colors ${form.type === "income" ? "bg-emerald-600 text-white" : "text-cream/60 hover:text-cream"}`}>
                Einzahlung
              </button>
              <button type="button" onClick={() => setForm((f) => ({ ...f, type: "expense" }))} data-testid="finance-form-type-expense"
                className={`py-2 rounded-lg text-sm font-semibold transition-colors ${form.type === "expense" ? "bg-red-600 text-white" : "text-cream/60 hover:text-cream"}`}>
                Auszahlung
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Betrag (CHF)</span>
                <Input type="number" min="0.01" step="0.01" value={form.amount} onChange={update("amount")} required data-testid="finance-form-amount" className={fieldClass} />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Datum</span>
                <Input type="date" value={form.date} onChange={update("date")} data-testid="finance-form-date" className={`${fieldClass} [color-scheme:dark]`} />
              </label>
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">{form.type === "income" ? "Von wem" : "An wen"}</span>
              <Input value={form.person} onChange={update("person")} data-testid="finance-form-person" className={fieldClass} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Zweck (für was)</span>
              <Input value={form.purpose} onChange={update("purpose")} data-testid="finance-form-purpose" className={fieldClass} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Ort (optional)</span>
              <Input value={form.place} onChange={update("place")} data-testid="finance-form-place" className={fieldClass} />
            </label>

            <DialogFooter className="mt-2">
              <button type="submit" disabled={saving} data-testid="finance-form-submit" className="btn-primary w-full disabled:opacity-70">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Speichern…</> : "Buchung speichern"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancePanel;
